// =============================================================================
// MeuDriver — FaqPage (Batch T1)
// FAQ pública: accordion por categoria, busca client-side, filtro de audiência
// Rota: /faq (sem login obrigatório)
// =============================================================================

import { useState } from 'react'
import { Search, ChevronDown, HelpCircle } from 'lucide-react'
import { Input } from '@/components/ui/input'
import { Badge } from '@/components/ui/badge'
import { Skeleton } from '@/components/ui/skeleton'
import { cn } from '@/lib/utils'
import { useFaqCategories, useFaqArticles, useSearchFaq } from '@/hooks/useFaq'
import type { FaqCategory } from '@/hooks/useFaq'
import { useAuth } from '@/hooks/useAuth'

// ─── Accordion item ───────────────────────────────────────────────────────────

function AccordionItem({
  question,
  answer,
}: {
  question: string
  answer: string
}) {
  const [open, setOpen] = useState(false)

  return (
    <div className="border border-border/60 rounded-xl overflow-hidden">
      <button
        className="flex w-full items-center justify-between gap-4 px-5 py-4 text-left text-sm font-medium text-gray-900 dark:text-gray-100 hover:bg-slate-50 dark:hover:bg-gray-800/50 transition-colors"
        onClick={() => setOpen((v) => !v)}
        data-testid="faq-accordion-trigger"
      >
        <span>{question}</span>
        <ChevronDown
          className={cn(
            'h-4 w-4 shrink-0 text-muted-foreground transition-transform duration-200',
            open && 'rotate-180',
          )}
        />
      </button>
      {open && (
        <div className="border-t border-border/40 px-5 py-4 text-sm text-muted-foreground leading-relaxed whitespace-pre-line">
          {answer}
        </div>
      )}
    </div>
  )
}

// ─── Categoria com seus artigos ───────────────────────────────────────────────

function CategorySection({
  category,
  audience,
}: {
  category: FaqCategory
  audience: 'all' | 'students' | 'instructors'
}) {
  const { data: articles = [], isLoading } = useFaqArticles(category.id)

  // Filtra pela audiência: mostra se category é 'all' ou coincide com audience selecionado
  const visible =
    category.target_audience === 'all' ||
    category.target_audience === audience

  if (!visible) return null
  if (!isLoading && articles.length === 0) return null

  return (
    <section className="space-y-2">
      <h2 className="text-base font-bold text-gray-900 dark:text-gray-100">
        {category.name}
      </h2>
      {isLoading ? (
        <div className="space-y-2">
          <Skeleton className="h-14 rounded-xl" />
          <Skeleton className="h-14 rounded-xl" />
        </div>
      ) : (
        <div className="space-y-2">
          {articles.map((a) => (
            <AccordionItem key={a.id} question={a.question} answer={a.answer} />
          ))}
        </div>
      )}
    </section>
  )
}

// ─── Página principal ─────────────────────────────────────────────────────────

export default function FaqPage() {
  const [searchQuery, setSearchQuery] = useState('')
  const [audience, setAudience] = useState<'all' | 'students' | 'instructors'>('all')
  const { user, profile } = useAuth()

  const { data: categories = [], isLoading: catsLoading } = useFaqCategories()
  const { data: searchResults, isLoading: searchLoading } = useSearchFaq(searchQuery)

  // Sugestão de audiência baseada no papel do usuário
  const suggestedAudience: 'all' | 'students' | 'instructors' =
    profile?.role === 'instructor' ? 'instructors' :
    profile?.role === 'student'    ? 'students'    : 'all'

  const isSearching = searchQuery.trim().length > 0

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-gray-950">
      {/* Hero */}
      <div className="bg-gradient-to-br from-brand-700 to-brand-900 py-16 px-4">
        <div className="max-w-2xl mx-auto text-center">
          <div className="flex justify-center mb-4">
            <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-white/15 ring-1 ring-white/20">
              <HelpCircle className="h-7 w-7 text-white" />
            </div>
          </div>
          <h1 className="text-3xl font-extrabold text-white mb-3">
            Central de Ajuda
          </h1>
          <p className="text-white/70 text-base mb-8">
            Encontre respostas para as dúvidas mais frequentes.
          </p>

          {/* Busca */}
          <div className="relative max-w-lg mx-auto">
            <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-white/50 pointer-events-none" />
            <Input
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Buscar pergunta ou resposta..."
              className="pl-10 bg-white/10 border-white/20 text-white placeholder:text-white/40 focus-visible:ring-white/30"
              data-testid="faq-search-input"
            />
          </div>
        </div>
      </div>

      {/* Conteúdo */}
      <div className="max-w-2xl mx-auto px-4 py-10 space-y-8">

        {/* Filtro de audiência (visível apenas sem busca ativa) */}
        {!isSearching && (
          <div className="flex gap-2 flex-wrap">
            {(['all', 'students', 'instructors'] as const).map((aud) => (
              <button
                key={aud}
                onClick={() => setAudience(aud)}
                className={cn(
                  'rounded-full px-4 py-1.5 text-xs font-semibold transition-colors border',
                  audience === aud
                    ? 'bg-brand-700 text-white border-brand-700'
                    : 'bg-white dark:bg-gray-900 text-muted-foreground border-border/60 hover:border-brand-400 hover:text-brand-700',
                )}
                data-testid={`faq-audience-filter-${aud}`}
              >
                {aud === 'all' ? 'Todos' : aud === 'students' ? 'Alunos' : 'Instrutores'}
              </button>
            ))}

            {/* Sugestão contextual quando logado */}
            {user && suggestedAudience !== 'all' && audience === 'all' && (
              <Badge
                variant="secondary"
                className="cursor-pointer text-xs"
                onClick={() => setAudience(suggestedAudience)}
              >
                Ver só para {suggestedAudience === 'students' ? 'alunos' : 'instrutores'}
              </Badge>
            )}
          </div>
        )}

        {/* Resultado da busca */}
        {isSearching ? (
          <section className="space-y-2">
            {searchLoading ? (
              <div className="space-y-2">
                <Skeleton className="h-14 rounded-xl" />
                <Skeleton className="h-14 rounded-xl" />
              </div>
            ) : searchResults.length === 0 ? (
              <p className="text-sm text-muted-foreground text-center py-10">
                Nenhum resultado para "<strong>{searchQuery}</strong>".
              </p>
            ) : (
              <>
                <p className="text-xs text-muted-foreground mb-3">
                  {searchResults.length} resultado{searchResults.length !== 1 ? 's' : ''}
                </p>
                {searchResults.map((a) => (
                  <AccordionItem key={a.id} question={a.question} answer={a.answer} />
                ))}
              </>
            )}
          </section>
        ) : (
          /* Listagem por categoria */
          catsLoading ? (
            <div className="space-y-6">
              {[1, 2, 3].map((i) => (
                <div key={i} className="space-y-2">
                  <Skeleton className="h-6 w-32 rounded" />
                  <Skeleton className="h-14 rounded-xl" />
                  <Skeleton className="h-14 rounded-xl" />
                </div>
              ))}
            </div>
          ) : (
            <div className="space-y-8">
              {categories.map((cat) => (
                <CategorySection key={cat.id} category={cat} audience={audience} />
              ))}
            </div>
          )
        )}
      </div>
    </div>
  )
}
